import { Component, Input, ElementRef, AfterViewInit, ViewChild, Output, EventEmitter } from '@angular/core';
import { fromEvent, merge } from 'rxjs';
import { switchMap, takeUntil, pairwise, startWith } from 'rxjs/operators';

@Component({
    selector: 'canvas-drawing',
    templateUrl: './canvas-drawing.component.html',
    standalone: false
})
export class CanvasDrawingComponent implements AfterViewInit {
  @ViewChild('canvas') canvas: ElementRef;

  @Input() width = 400;
  @Input() height = 200;
  @Input() data: string;
  @Output() drawing = new EventEmitter<string>();

  private cx: CanvasRenderingContext2D;
  private canvasEl: HTMLCanvasElement;

  ngAfterViewInit() {
    this.canvasEl = this.canvas.nativeElement;
    this.cx = this.canvasEl.getContext('2d');

    this.canvasEl.width = this.width;
    this.canvasEl.height = this.height;

    this.cx.lineWidth = 2;
    this.cx.lineCap = 'round';
    this.cx.strokeStyle = '#000';

    this.setInitialData();
    this.captureEvents();
  }

  clear() {
    this.cx.clearRect(0, 0, this.width, this.height);
    this.drawing.emit('');
  }

  private captureEvents() {
    this.canvasEl.addEventListener('touchmove', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent('mousemove', {
        clientX: touch.clientX,
        clientY: touch.clientY
      });
      this.canvasEl.dispatchEvent(mouseEvent);
    });
    const mouseDown = merge(fromEvent(this.canvasEl, 'mousedown'), fromEvent(this.canvasEl, 'touchstart'));
    const mouseUp = merge(
      fromEvent(this.canvasEl, 'mouseup'),
      fromEvent(this.canvasEl, 'mouseleave'),
      fromEvent(this.canvasEl, 'touchend'),
      fromEvent(this.canvasEl, 'touchcancel')
    );
    mouseDown
      .pipe(
        switchMap((e: MouseEvent) => {
          return fromEvent(this.canvasEl, 'mousemove').pipe(startWith(e, e), takeUntil(mouseUp), pairwise());
        })
      )
      .subscribe((res: [MouseEvent, MouseEvent]) => {
        this.drawFromEvents(res);
        this.drawing.emit(this.canvasEl.toDataURL());
      });
  }

  private drawFromEvents(res: [MouseEvent, MouseEvent]) {
    const rect = this.canvasEl.getBoundingClientRect();
    const prevPos = {
      x: res[0].clientX - rect.left,
      y: res[0].clientY - rect.top
    };

    const currentPos = {
      x: res[1].clientX - rect.left,
      y: res[1].clientY - rect.top
    };
    this.drawOnCanvas(prevPos, currentPos);
  }

  private drawOnCanvas(prevPos: { x: number; y: number }, currentPos: { x: number; y: number }) {
    if (!this.cx) {
      return;
    }

    this.cx.beginPath();

    if (prevPos) {
      this.cx.moveTo(prevPos.x, prevPos.y);
      this.cx.lineTo(currentPos.x, currentPos.y);
      this.cx.stroke();
    }
  }

  private setInitialData() {
    if (!this.data) return;

    const image = new Image();
    image.src = this.data;
    this.cx.drawImage(image, 0, 0);
  }
}
